/* OnSumn v2 — Footer */

/* The footer's THE BAY column is a set of ?city= deep links, so it has to
   speak the same city vocabulary as the header pills and the resolver. It used
   to carry its own hardcoded 7 plus a hand-written label→param table — a third
   copy of the list, which is how "The Town" and "Oakland" both shipped as
   separate rows pointing at the same filter. */
import {
  cityParam, toDisplayCity, NAV_FALLBACK_CITIES,
} from '../lib/city-registry.js';

// Self-aware dark mode — Footer now ships on every page (index, venue, flows),
// each of which manages theme independently. Mirrors VenuePage.jsx:788-800
// pattern: read [data-theme] at mount + observe attribute changes.
function useFooterTheme() {
  const initial = typeof document !== 'undefined'
    ? document.documentElement.getAttribute('data-theme') !== 'light'
    : true;
  const [darkMode, setDarkMode] = React.useState(initial);
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    const update = () => setDarkMode(html.getAttribute('data-theme') !== 'light');
    update();
    const observer = new MutationObserver(update);
    observer.observe(html, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);
  return darkMode;
}

/* How many cities the footer column lists. The column is a nav aid, not an
   index — past about seven links it stops being scannable and the tail cities
   (one or two events each) crowd out the rooms most readers came for. */
const FOOTER_CITY_LIMIT = 7;

function Footer({ darkMode: darkModeProp, radius = 14, cities }) {
  // Prop wins when provided (homepage passes it through); otherwise the
  // footer reads document data-theme so venue/flows can drop in <Footer />
  // with no prop wiring.
  const observed = useFooterTheme();
  const darkMode = typeof darkModeProp === 'boolean' ? darkModeProp : observed;

  const textColor = darkMode ? 'var(--on-surface)' : '#1a1a1a';
  /* G3a (Zora/Lux + Iris): was 0.3 white / #999 — 2.63:1 / 2.78:1, below
     even the 3:1 large-text bar, and the bar now carries an interactive
     control (the disclosure door). 0.5 white = 5.37:1, #6E6E6E = 4.97:1;
     the muted-legal register survives, it just stops being invisible. */
  const mutedColor = darkMode ? 'rgba(255,255,255,0.5)' : '#6E6E6E';

  // Contact email — michael@onsumn.com via Proton Mail Plus custom domain.
  // SPF / DKIM / DMARC verified pass-pass-pass on 2026-05-29 outbound test
  // (Return-Path, dkim d=onsumn.com s=protonmail, p=REJECT alignment honored).
  const CONTACT_EMAIL = 'michael@onsumn.com';

  /* G3a — WHAT THIS BROWSER KNOWS. The disclosure panel's ONE entry point:
     the footer ships on all three pages, so one link here covers the site
     (≤2 taps from anywhere). A door, not a notice — plain label, no badge,
     no count, no dot. Panel component is window-attached by
     BrowserKnows.jsx (loaded beside Sections.js on every page); guarded so
     a page missing the script degrades to no link action rather than a
     crash. */
  const [bkOpen, setBkOpen] = React.useState(false);

  /* WHAT FOLLOWS YOU — the second door, beside the first. The pair explains
     the whole product model in two footer lines: one names what stays on the
     device, the other names what travels. Same strap, same register, same
     44px hit; no badge, no count, no motion (Off-Limits §9 applies to this
     door exactly as it does to header chrome).

     The panel it opens is a plain in-flow <section> rendered ABOVE the
     <footer> element — the colophon unfolds. Not a modal, not a portal, and
     it mounts ONLY from this activation: no deep link, no scroll trigger, no
     timer. /flows' contextual door reaches it through one event, which is
     also the only other way in. */
  const [afyOpen, setAfyOpen] = React.useState(false);
  const [afyScroll, setAfyScroll] = React.useState(false);
  const [afyAttached, setAfyAttached] = React.useState(
    () => !!(typeof window !== 'undefined' && window.OnSumnAccount && window.OnSumnAccount.isAttached())
  );
  const afyDoorRef = React.useRef(null);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const open = (e) => {
      setAfyScroll(!!(e && e.detail && e.detail.scroll));
      setAfyOpen(true);
    };
    window.addEventListener('onsumn:open-what-follows-you', open);
    const acct = window.OnSumnAccount;
    const off = (acct && typeof acct.onProfileChange === 'function')
      ? acct.onProfileChange(() => setAfyAttached(!!acct.isAttached()))
      : null;
    return () => {
      window.removeEventListener('onsumn:open-what-follows-you', open);
      if (off) off();
    };
  }, []);

  /* Closing hands focus back to the door that opened it (WCAG 2.4.3). */
  const closeAfy = React.useCallback(() => {
    setAfyOpen(false);
    setAfyScroll(false);
    if (typeof window !== 'undefined' && window.OnSumnAccount) {
      setAfyAttached(!!window.OnSumnAccount.isAttached());
    }
    if (afyDoorRef.current) afyDoorRef.current.focus();
  }, []);

  /* THE BAY column + the wordmark strap, DERIVED. `cities` (canonical,
     count-ordered) arrives from whichever page owns the feed; /venue and
     /flows have no feed, so those fall back to the declared nav list.
     That fallback is not a claim about any event — it is a link, and the worst
     a stale entry can do is land the reader on an honest empty state that
     names the city. Labels are re-canonicalized so one shouty upstream row
     cannot spell a footer link. */
  const navCities = (Array.isArray(cities) && cities.length ? cities : NAV_FALLBACK_CITIES)
    .map(toDisplayCity)
    .filter(Boolean)
    .slice(0, FOOTER_CITY_LIMIT);

  const footerEl = (
    <footer style={{
      padding: '56px 32px 28px',
      background: darkMode ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.5)',
      borderTop: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
    }}>
      <div className="footer-grid" style={{
        maxWidth: 1280, margin: '0 auto',
        display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 48,
      }}>
        <div>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 900,
            color: textColor, textTransform: 'uppercase', marginBottom: 12,
          }}>OnSumn</div>
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: '20px',
            color: textColor, marginBottom: 16,
          }}>Built in the Bay. We stay OnSumn.</div>
          {/* Same derived list as the column below — this strap used to be a
              hand-typed six that quietly named Vallejo (1 live event) as a
              headline city while Berkeley (41) went unmentioned. */}
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: textColor }}>
            {navCities.slice(0, 6).join(' · ')}
          </div>
        </div>
        {[
          { kind: 'city', title: 'THE BAY', links: navCities },
          { kind: 'cat', title: 'CATEGORIES', links: ['Music', 'Cards', 'Art', 'Food', 'Nightlife', 'Community'] },
        ].map(col => {
          // Label → URL param. THE BAY maps to ?city=, CATEGORIES to ?cat=.
          // The city half is now ONE call into the registry — cityParam is the
          // same rule the resolver reads back, so a footer link cannot drift
          // out of the vocabulary the homepage accepts. The old hand-written
          // table is gone with the duplicate "The Town" row it needed: Bay
          // slang lives in cityToTag now (SF only since 2026-08-20 — Oakland reads OAKLAND),
          // and a second link to the same filter was only ever a second way to
          // get the vocabulary wrong.
          const hrefFor = (l) => (col.kind === 'cat'
            ? `/?cat=${encodeURIComponent(l)}#events`
            : `/?city=${encodeURIComponent(cityParam(l))}#events`);
          return (
          <div key={col.title}>
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              color: mutedColor, marginBottom: 16,
            }}>{col.title}</div>
            {col.links.map(l => (
              <a key={l} href={hrefFor(l)} style={{
                display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
                color: textColor, padding: '6px 0', cursor: 'pointer', transition: 'color 0.15s',
                textDecoration: 'none',
              }}
              onMouseEnter={e => e.target.style.color = 'var(--international-orange-ink)'}
              onMouseLeave={e => e.target.style.color = textColor}
              onFocus={e => e.target.style.color = 'var(--international-orange-ink)'}
              onBlur={e => e.target.style.color = textColor}
              >{l}</a>
            ))}
          </div>
        );})}
        {/* Contact column — mailto anchor (not a button) so PC right-click
            "Copy email address" works, and so it follows the rest of the
            web's mailto conventions. */}
        <div>
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            color: mutedColor, marginBottom: 16,
          }}>GET IN TOUCH</div>
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 12, lineHeight: '18px',
            color: textColor, marginBottom: 10,
          }}>Need an oop? Hit us up.</div>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            style={{
              display: 'inline-block',
              fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
              color: textColor,
              textDecoration: 'none',
              borderBottom: '1px dotted currentColor',
              padding: '4px 0',
              transition: 'color 0.15s, border-color 0.15s',
              wordBreak: 'break-all',
            }}
            onMouseEnter={e => { e.target.style.color = 'var(--international-orange-ink)'; }}
            onMouseLeave={e => { e.target.style.color = textColor; }}
            onFocus={e => { e.target.style.color = 'var(--international-orange-ink)'; }}
            onBlur={e => { e.target.style.color = textColor; }}
          >{CONTACT_EMAIL}</a>
        </div>
      </div>
      <div style={{
        maxWidth: 1280, margin: '40px auto 0', paddingTop: 20,
        borderTop: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
        fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500,
        letterSpacing: '0.05em', textTransform: 'uppercase', color: mutedColor,
        display: 'flex', flexWrap: 'wrap', gap: '6px 18px', alignItems: 'baseline',
      }}>
        <span>BORN IN THE BAY. © 2026 ONSUMN</span>
        {/* Privacy — one site-wide entry point to /privacy. Ships on every page
            carrying the footer (index/venue/flows), so the policy is ≤1 tap from
            anywhere (CalOPPA "conspicuously posted"). Static /privacy route, not
            a React mount. Strap textTransform uppercases the label. */}
        <a
          href="/privacy"
          style={{ color: mutedColor, textDecoration: 'none', borderBottom: '1px dotted currentColor', transition: 'color 0.15s' }}
          onMouseEnter={e => { e.target.style.color = 'var(--international-orange-ink)'; }}
          onMouseLeave={e => { e.target.style.color = mutedColor; }}
          onFocus={e => { e.target.style.color = 'var(--international-orange-ink)'; }}
          onBlur={e => { e.target.style.color = mutedColor; }}
        >Privacy</a>
        {/* C2: venue coordinates derive from the Overture Maps places theme
            (lib/licensed-sources.js `overture-places` — the attribution
            guard fails the build if this credit disappears from a page
            that consumes events.json coordinates). */}
        <a
          href="https://overturemaps.org"
          target="_blank"
          rel="noopener"
          aria-label="Venue location data from the Overture Maps Foundation"
          style={{ color: mutedColor, textDecoration: 'none', borderBottom: '1px dotted currentColor' }}
        >VENUE DATA © OVERTURE MAPS FOUNDATION</a>
        <button
          type="button"
          data-bk-open
          className="focus-double-ring"
          onClick={() => setBkOpen(true)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            font: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit',
            color: mutedColor,
            /* 44px hit area with no visual pitch change (Zora spec). The
               dotted affordance is a text-decoration, not a border — a
               border rides the padding box and detaches 16px below the
               label (Zora residual). */
            padding: '16px 0', margin: '-12px 0',
            textDecoration: 'underline dotted', textUnderlineOffset: 3,
          }}
        >WHAT THIS BROWSER KNOWS</button>
        <button
          type="button"
          data-afy-open
          ref={afyDoorRef}
          className="focus-double-ring"
          onClick={() => { setAfyScroll(false); setAfyOpen(true); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            font: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit',
            color: mutedColor,
            padding: '16px 0', margin: '-12px 0',
            textDecoration: 'underline dotted', textUnderlineOffset: 3,
          }}
        >
          {afyAttached && (
            /* Device A tick — the ENTIRE site-chrome acknowledgment of an
               attached setup. It is there or it is not: no pulse, no fade,
               no count. Decorative by construction (aria-hidden); the state
               reaches screen readers through the visually-hidden suffix
               below, which is the only SR signal.
               Colour is the house token per theme, as a LITERAL so the
               contrast gate can compute it: dark --amber-gold #D57800 =
               6.25:1 on the footer band; light --inverse-primary #8E4E00 =
               6.05:1. #D57800 in LIGHT computes 3.01:1 — a coin-flip at the
               3:1 non-text floor, so it is not used there (Lux). */
            <span
              aria-hidden="true"
              data-afy-tick
              style={{
                display: 'inline-block', width: 6, height: 6, marginRight: 10,
                verticalAlign: '0.08em',
                background: darkMode ? '#D57800' : '#8E4E00',
                forcedColorAdjust: 'auto',
              }}
            />
          )}
          WHAT FOLLOWS YOU
          {afyAttached && (
            <span style={{
              position: 'absolute', width: 1, height: 1, margin: -1, padding: 0,
              overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
            }}>{' — setup attached'}</span>
          )}
        </button>
      </div>
      {bkOpen && typeof window !== 'undefined' && typeof window.BrowserKnowsPanel === 'function' && (
        <window.BrowserKnowsPanel darkMode={darkMode} onClose={() => setBkOpen(false)} />
      )}
    </footer>
  );

  /* The panel sits ABOVE the <footer> in document order — an in-flow
     section, never an overlay. Guarded: a page that does not ship
     dist/AccountLite.js degrades to a door that does nothing, not a crash
     (the BrowserKnowsPanel precedent one line up). */
  return (
    <React.Fragment>
      {afyOpen && typeof window !== 'undefined' && typeof window.AccountLitePanel === 'function' && (
        <window.AccountLitePanel
          darkMode={darkMode}
          radius={radius}
          scrollOnMount={afyScroll}
          onClose={closeAfy}
        />
      )}
      {footerEl}
    </React.Fragment>
  );
}
Footer.propTypes = {
  darkMode: PropTypes.bool,
  radius: PropTypes.number,
  /* Canonical city names, count-ordered. Optional: pages without a feed fall
     back to lib/city-registry#NAV_FALLBACK_CITIES. */
  cities: PropTypes.arrayOf(PropTypes.string),
};

window.Footer = Footer;
